import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Button } from "../components/Button";
import { parseMarkdown } from "../utils/markdown";
import type { ProductWithImages } from "../types";
import { usePageTitle } from "../hooks/usePageTitle";

export function ProductDetail() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<ProductWithImages | null>(null);

  usePageTitle(product?.name || "Product");
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [settings, setSettings] = useState<{
    product_page_notes?: string;
    product_page_return_policy?: string;
  }>({});

  useEffect(() => {
    fetchProduct();
    fetchSettings();
  }, [slug]);

  const fetchSettings = async () => {
    try {
      const response = await fetch("/api/settings");
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await fetch(`/api/products/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-96 bg-gray-200 rounded mb-4" />
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-1/4" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold">Product not found</h1>
        <Link to="/" className="text-blue-600 hover:underline mt-4 block">
          Return to home
        </Link>
      </div>
    );
  }

  const isOutOfStock = product.inventory === 0;
  const displayImage =
    product.images[selectedImage]?.image_path || product.primaryImage;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/" className="hover:underline">
          Home
        </Link>{" "}
        /{" "}
        <Link to={`/products/${product.category}`} className="hover:underline">
          {product.category}
        </Link>{" "}
        / {product.name}
      </nav>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Image Gallery */}
        <div>
          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4">
            {displayImage ? (
              <img
                src={displayImage}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                No Image
              </div>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {product.images.map((img, index) => (
                <button
                  key={img.id}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square rounded overflow-hidden border-2 ${
                    selectedImage === index
                      ? "border-black"
                      : "border-transparent"
                  }`}
                >
                  <img
                    src={img.image_path}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <h1 className="text-4xl font-bold mb-4">{product.name}</h1>
          <p className="text-3xl font-bold mb-6">${product.price.toFixed(2)}</p>

          {product.description && (
            <div
              className="prose prose-sm text-gray-700 mb-6"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(product.description) }}
            />
          )}

          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2">
              Stock: {product.inventory > 0 ? `${product.inventory} available` : "Out of stock"}
            </p>
          </div>

          {!isOutOfStock && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Quantity</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 border border-gray-300 rounded hover:bg-gray-100"
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max={product.inventory}
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(
                      Math.min(product.inventory, Math.max(1, parseInt(e.target.value) || 1))
                    )
                  }
                  className="w-20 text-center border border-gray-300 rounded py-2"
                />
                <button
                  onClick={() =>
                    setQuantity(Math.min(product.inventory, quantity + 1))
                  }
                  className="w-10 h-10 border border-gray-300 rounded hover:bg-gray-100"
                >
                  +
                </button>
              </div>
            </div>
          )}

          <Button
            variant="primary"
            className="w-full text-lg"
            onClick={() => addToCart(product, quantity)}
            disabled={isOutOfStock}
          >
            {isOutOfStock ? "Out of Stock" : "Add to Cart"}
          </Button>

          {/* Product Page Notes */}
          {(settings.product_page_notes || settings.product_page_return_policy) && (
            <div className="mt-8 space-y-6 border-t pt-6">
              {settings.product_page_notes && (
                <div>
                  <h3 className="text-lg font-bold mb-2">Important Notes</h3>
                  <div
                    className="prose prose-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(settings.product_page_notes) }}
                  />
                </div>
              )}

              {settings.product_page_return_policy && (
                <div>
                  <h3 className="text-lg font-bold mb-2">Return Policy</h3>
                  <div
                    className="prose prose-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(settings.product_page_return_policy) }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
